<template>
  <ca-rollover ref="rollover" title="Cadastro de responsável">
    <ca-loader v-if="showLoading"></ca-loader>

    <ca-form
      v-else
      :key="formKey"
      :on-submit="onSubmit"
      :on-submit-success="onSubmitSuccess"
      :on-submit-error="onSubmitError"
      :error-message="errorMessage">
      <ca-row>
        <ca-col size="6">
          <ca-field label="Tipo inscrição">
            <ca-select v-model="responsible.registerType.name">
              <ca-option
                v-for="insciptionType in misc.companyInscriptionType"
                :key="insciptionType.value"
                :value="insciptionType.value">
                {{ insciptionType.description }}
              </ca-option>
            </ca-select>
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Inscrição">
            <cnpj-input
              v-if="isLegalInscription"
              v-model="responsible.register"
              required></cnpj-input>
            <ca-cpf-input
              v-else-if="isIndividualInscription"
              v-model="responsible.register"
              required></ca-cpf-input>
            <ca-input
              v-else
              v-model="responsible.register"
              maxlength="14"
              required></ca-input>
          </ca-field>
        </ca-col>
      </ca-row>

      <ca-row>
        <ca-col size="6">
          <ca-field label="Nome/Razão Social">
            <ca-input
              v-model="responsible.name"
              maxlength="60"
              required>
            </ca-input>
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Telefone">
            <ca-input
              v-model="responsible.phone"
              v-mask="['(##) ####-####', '(##) #####-####']"
              required>
            </ca-input>
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="CEP">
            <cep-input v-model="responsible.zipCode" @on-fetch="onCepFetch" />
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Logradouro">
            <ca-input v-model="responsible.street" maxlength="80" />
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Cidade">
            <ca-input v-model="responsible.city" maxlength="30" />
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Estado">
            <ca-search-select
              v-model="responsible.state.name"
              placeholder="Selecione"
              :selected-option="selectedUf"
              required>
              <ca-option
                v-for="state in misc.uf"
                :key="state.name"
                :value="state.name"
                placeholder="Selecione">
                {{ state.description }}
              </ca-option>
            </ca-search-select>
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Bairro">
            <ca-input v-model="responsible.neighborhood" maxlength="30" />
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Número">
            <ca-input v-model="responsible.number" maxlength="8" />
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Complemento">
            <ca-input v-model="responsible.complement" maxlength="30" />
          </ca-field>
        </ca-col>

        <ca-col size="6">
          <ca-field label="Compartilhado">
            <ca-checkbox v-model="responsible.shared" />
          </ca-field>
        </ca-col>
      </ca-row>

      <ca-footer>
        <ca-row vertical-offset="2">
          <ca-col size="2">
            <ca-button full-width @click="onClose">Cancelar</ca-button>
          </ca-col>
          <ca-col offset="8" size="2">
            <ca-submit-button>Salvar</ca-submit-button>
          </ca-col>
        </ca-row>
      </ca-footer>
    </ca-form>
  </ca-rollover>
</template>

<script>
import { buildOpenDialog } from 'packages/common/ca-components';
import {
  initFormObject, getBoxInformationErrorMessage, getErrorMessageFromResponse,
} from 'packages/common/utils';
import { getResponsible, createResponsible, editResponsible } from './responsibleService';

const ResponsibleFormRollover = {
  name: 'ResponsibleFormRollover',
  props: {
    responsibleId: [String, Number],
  },
  data() {
    return {
      formKey: 0,
      responsible: {
        ...initFormObject(['registerType', 'state']),
        phone: '',
      },
      selectedUf: null,
      errorMessage: null,
      showLoading: false,
    };
  },
  computed: {
    hasResponsibleId() {
      return !!this.responsibleId;
    },
    misc() {
      return this.$store.state.misc;
    },
    isLegalInscription() {
      return this.responsible?.registerType?.name === 'J';
    },
    isIndividualInscription() {
      return this.responsible?.registerType?.name === 'F';
    },
  },
  watch: {
    'responsible.registerType.name': function fn() {
      this.refreshForm();
    },
  },
  created() {
    this.initData();
  },
  methods: {
    initData() {
      this.$store.dispatch('misc/getCompanyInscriptionType', 'TODOS');
      this.$store.dispatch('misc/getUf');

      if (this.hasResponsibleId) {
        this.setResponsible();
      }
    },
    onClose() {
      this.$refs.rollover.close();
    },
    onCepFetch(response) {
      const {
        neighborhood, city, streetAddress, state,
      } = response;

      this.$set(this.responsible, 'street', streetAddress);
      this.$set(this.responsible, 'neighborhood', neighborhood);
      this.$set(this.responsible, 'city', city);
      this.selectedUf = { label: state, value: state };
    },
    async setResponsible() {
      this.showLoading = true;
      try {
        this.responsible = await getResponsible(this.responsibleId);
      } catch (error) {
        this.$toaster.error(getErrorMessageFromResponse(error));
      } finally {
        this.showLoading = false;
      }
    },
    onSubmit() {
      return this.hasResponsibleId
        ? editResponsible(this.responsibleId, this.responsible)
        : createResponsible(this.responsible);
    },
    onSubmitSuccess() {
      this.$toaster.success('Dados salvos com sucesso');
      this.$emit('completed');
      this.onClose();
    },
    onSubmitError(error) {
      this.errorMessage = getBoxInformationErrorMessage(error);
    },
    refreshForm() {
      this.formKey++;
    },
  },
};

ResponsibleFormRollover.open = buildOpenDialog(ResponsibleFormRollover);

export default ResponsibleFormRollover;
</script>
